import React from 'react';
import { Activity, DollarSign, Briefcase, Heart, Zap, Star, ArrowRight } from 'lucide-react';
import { HOROSCOPE_TRANSLATIONS } from '../../../../languages/horoscope';

interface AspectData {
    score: string;
    prediction: string;
    advice: string;
}

interface HoroscopeDetailsProps {
    aspects: Record<string, AspectData>;
    language: string;
}

const aspectIcons: Record<string, any> = {
    health: Activity,
    wealth: DollarSign,
    career: Briefcase,
    relationship: Heart,
    luck: Zap,
};

const aspectColors: Record<string, { text: string; border: string; bg: string; bar: string }> = {
    health: {
        text: 'text-emerald-400',
        border: 'border-emerald-500/20',
        bg: 'bg-emerald-500/5',
        bar: 'bg-emerald-400'
    },
    wealth: {
        text: 'text-amber-400',
        border: 'border-amber-500/20',
        bg: 'bg-amber-500/5',
        bar: 'bg-amber-400'
    },
    career: {
        text: 'text-blue-400',
        border: 'border-blue-500/20',
        bg: 'bg-blue-500/5',
        bar: 'bg-blue-400'
    },
    relationship: {
        text: 'text-pink-400',
        border: 'border-pink-500/20',
        bg: 'bg-pink-500/5',
        bar: 'bg-pink-400'
    },
    luck: {
        text: 'text-purple-400',
        border: 'border-purple-500/20',
        bg: 'bg-purple-500/5',
        bar: 'bg-purple-400'
    },
};

const HoroscopeDetails: React.FC<HoroscopeDetailsProps> = ({ aspects, language }) => {
    const langKey = language === 'hindi' ? 'Hindi' : 'English';
    const t = HOROSCOPE_TRANSLATIONS[langKey];

    return (
        <>
            <h3 className="text-xl font-bold text-white mt-8 mb-4 px-2 font-serif">{t.detailedAnalysis}</h3>
            <div className="grid grid-cols-1 gap-4">
                {Object.entries(aspects).map(([rawKey, value], index) => {
                    const key = rawKey.toLowerCase();
                    const Icon = aspectIcons[key] || Star;
                    const colors = aspectColors[key] || aspectColors.luck;
                    const rawScore = parseInt(value.score);
                    const score = isNaN(rawScore) ? 0 : rawScore;

                    return (
                        <div
                            key={key}
                            className={`rounded-3xl p-6 border bg-cosmic-900/50 backdrop-blur-sm hover:bg-cosmic-900/70 transition-colors duration-300 group ${colors.border}`}
                        >
                            <div className="flex flex-col md:flex-row gap-6">
                                {/* Left: Score & Icon */}
                                <div className="md:w-48 flex-shrink-0 flex flex-col items-center justify-center p-4 rounded-2xl bg-black/20">
                                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 ${colors.bg}`}>
                                        <Icon size={24} className={colors.text} />
                                    </div>
                                    <h4 className="capitalize font-bold text-lg text-white mb-1 font-sans">{t[key] || key}</h4>
                                    <div className="flex items-end gap-1 font-sans">
                                        <span className="text-3xl font-black text-white">{score}</span>
                                        <span className="text-sm text-white/60 mb-1">/100</span>
                                    </div>
                                    <div className="w-full h-1.5 bg-white/10 rounded-full mt-3 overflow-hidden">
                                        <div
                                            className={`h-full rounded-full ${colors.bar}`}
                                            style={{ width: `${score}%` }}
                                        />
                                    </div>
                                </div>

                                {/* Right: Content */}
                                <div className="flex-1">
                                    <p className="text-purple-100/80 leading-relaxed mb-4 font-sans font-light">
                                        {value.prediction}
                                    </p>
                                    <div className={`flex items-start gap-3 p-4 rounded-xl ${colors.bg}`}>
                                        <ArrowRight size={16} className={`mt-0.5 flex-shrink-0 ${colors.text}`} />
                                        <div className="font-sans">
                                            <p className={`text-xs font-bold uppercase tracking-wider mb-1 ${colors.text}`}>{t.astrologicalAdvice}</p>
                                            <p className="text-sm text-purple-100/70 italic">"{value.advice}"</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </>
    );
};

export default HoroscopeDetails;
